import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  Gavel,
  FilterList,
  ExpandMore,
  ExpandLess,
  Visibility,
  ContentCopy,
  Download,
} from '@mui/icons-material';
import {
  searchJurisprudence,
  getJurisprudenceDetail,
  listCourts,
  listLegalDomains,
  type JurisprudenceSearchResult,
  type JurisprudenceDetail,
} from '../../services/jurisprudenceService';
import { FeedbackWidget } from '../../components/feedback';

const JurisprudenceSearchPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<JurisprudenceSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [courtFilter, setCourtFilter] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [courts, setCourts] = useState<string[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<JurisprudenceDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const itemsPerPage = 12;

  useEffect(() => {
    loadFilters();
  }, []);

  const loadFilters = async () => {
    try {
      const [courtsData, domainsData] = await Promise.all([
        listCourts(),
        listLegalDomains(),
      ]);
      setCourts(courtsData);
      setDomains(domainsData);
    } catch (err) {
      console.error('Erro ao carregar filtros:', err);
    }
  };

  const handleSearch = async () => {
    if (searchTerm.length < 3) {
      return;
    }

    try {
      setLoading(true);
      const data = await searchJurisprudence(searchTerm, {
        court_name: courtFilter || undefined,
        legal_domain: domainFilter || undefined,
        skip: (page - 1) * itemsPerPage,
        limit: itemsPerPage,
      });
      setResults(data.items);
      setTotal(data.total);
    } catch (err: any) {
      console.error('Erro ao buscar:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (id: number) => {
    try {
      setLoadingDetail(true);
      setDetailDialog(true);
      const detail = await getJurisprudenceDetail(id);
      setSelectedItem(detail);
    } catch (err: any) {
      console.error('Erro ao carregar detalhes:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCopyEmenta = (item: JurisprudenceSearchResult) => {
    navigator.clipboard.writeText(item.ementa);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (searchTerm.length >= 3) {
      const debounce = setTimeout(() => {
        handleSearch();
      }, 500);
      return () => clearTimeout(debounce);
    } else {
      setResults([]);
      setTotal(0);
    }
  }, [searchTerm, courtFilter, domainFilter, page]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header - Compacto quando há resultados */}
      <Box sx={{ 
        mb: results.length > 0 ? 2 : 3, 
        textAlign: 'center',
        transition: 'all 0.3s ease'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          mb: results.length > 0 ? 1 : 1.5 
        }}>
          <Gavel sx={{ 
            fontSize: results.length > 0 ? 32 : 40, 
            color: 'primary.main', 
            mr: 1.5,
            transition: 'font-size 0.3s ease'
          }} />
          <Typography 
            variant={results.length > 0 ? "h5" : "h4"} 
            sx={{ 
              fontWeight: 'bold',
              transition: 'font-size 0.3s ease'
            }}
          >
            Jurisprudências Validadas
          </Typography>
        </Box>
        {results.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Busque em nossa base de dados validada por especialistas
          </Typography>
        )}
      </Box>

      {/* Busca e Filtros */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          {/* Campo de Busca */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Buscar jurisprudências"
              placeholder="Digite palavras-chave: IPTU, tributário, execução, contrato..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && searchTerm.length >= 3) {
                  handleSearch();
                }
              }}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              helperText={
                searchTerm.length > 0 && searchTerm.length < 3 
                  ? 'Digite pelo menos 3 caracteres' 
                  : 'Pressione Enter ou clique em Buscar'
              }
              error={searchTerm.length > 0 && searchTerm.length < 3}
            />
          </Grid>

          {/* Filtros e Botão */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Tribunal</InputLabel>
              <Select
                value={courtFilter}
                label="Tribunal"
                onChange={(e) => setCourtFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>Todos os Tribunais</em>
                </MenuItem>
                {courts.map((court) => (
                  <MenuItem key={court} value={court}>
                    {court}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Área do Direito</InputLabel>
              <Select
                value={domainFilter}
                label="Área do Direito"
                onChange={(e) => setDomainFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>Todas as Áreas</em>
                </MenuItem>
                {domains.map((domain) => (
                  <MenuItem key={domain} value={domain}>
                    {domain}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Search />}
              onClick={handleSearch}
              disabled={searchTerm.length < 3 || loading}
              sx={{ height: '56px' }}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Filtros Ativos */}
      {(courtFilter || domainFilter) && (
        <Box sx={{ mb: 3, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FilterList sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
            Filtros ativos:
          </Typography>
          {courtFilter && (
            <Chip
              label={courtFilter}
              onDelete={() => setCourtFilter('')}
              size="small"
              color="primary"
              icon={<Gavel />}
            />
          )}
          {domainFilter && (
            <Chip
              label={domainFilter}
              onDelete={() => setDomainFilter('')}
              size="small"
              color="secondary"
            />
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setCourtFilter('');
              setDomainFilter('');
            }}
            sx={{ ml: 'auto' }}
          >
            Limpar todos
          </Button>
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Mensagens */}
      {!loading && searchTerm.length >= 3 && results.length === 0 && (
        <Alert severity="info" sx={{ my: 4 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Nenhuma jurisprudência encontrada
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Termo buscado: "{searchTerm}"
            {courtFilter && ` • Tribunal: ${courtFilter}`}
            {domainFilter && ` • Área: ${domainFilter}`}
          </Typography>
          <Typography variant="body2">
            Sugestões:
          </Typography>
          <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
            <li>Use termos mais genéricos (ex: "contrato" ao invés de "contratos de prestação de serviços")</li>
            <li>Verifique a ortografia</li>
            {(courtFilter || domainFilter) && <li>Remova os filtros ativos</li>}
            <li>Tente sinônimos (ex: "imposto" → "tributo", "dano" → "prejuízo")</li>
          </ul>
        </Alert>
      )}

      {!loading && searchTerm.length === 0 && (
        <Box sx={{ textAlign: 'center', my: 8 }}>
          <Gavel sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Digite um termo para iniciar a busca
          </Typography>
        </Box>
      )}

      {/* Resultados */}
      {!loading && results.length > 0 && (
        <>
          <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'primary.50', borderLeft: 4, borderColor: 'primary.main' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                  {total} jurisprudência{total !== 1 ? 's' : ''} encontrada{total !== 1 ? 's' : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {searchTerm && `Buscando por: "${searchTerm}"`}
                  {courtFilter && ` • Tribunal: ${courtFilter}`}
                  {domainFilter && ` • Área: ${domainFilter}`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  icon={<FilterList />}
                  label={`Página ${page} de ${Math.ceil(total / itemsPerPage)}`}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={`${results.length} exibidos`}
                  variant="outlined"
                  size="small"
                  color="primary"
                />
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={3}>
            {results.map((item) => (
              <Grid item xs={12} md={6} lg={4} key={item.id}>
                <Card
                  elevation={expandedCard === item.id ? 8 : 2}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s',
                    '&:hover': {
                      elevation: 6,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardContent sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        mb: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        minHeight: '3rem',
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
                      <Chip label={item.court_name} size="small" color="primary" />
                      {item.legal_domain && (
                        <Chip label={item.legal_domain} size="small" color="secondary" />
                      )}
                      {item.judgment_date && (
                        <Chip label={formatDate(item.judgment_date)} size="small" variant="outlined" />
                      )}
                    </Stack>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: expandedCard === item.id ? 'unset' : 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mb: 1,
                      }}
                    >
                      {item.ementa}
                    </Typography>

                    {item.keywords && item.keywords.length > 0 && (
                      <Collapse in={expandedCard === item.id} timeout="auto">
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                          Palavras-chave:
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {item.keywords.slice(0, 5).map((keyword, idx) => (
                            <Chip key={idx} label={keyword} size="small" />
                          ))}
                        </Stack>
                      </Collapse>
                    )}
                  </CardContent>

                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Button
                      size="small"
                      startIcon={expandedCard === item.id ? <ExpandLess /> : <ExpandMore />}
                      onClick={() => setExpandedCard(expandedCard === item.id ? null : item.id)}
                    >
                      {expandedCard === item.id ? 'Menos' : 'Mais'}
                    </Button>

                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        color={copiedId === item.id ? 'success' : 'default'}
                        onClick={() => handleCopyEmenta(item)}
                        title="Copiar ementa"
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleViewDetail(item.id)}
                        title="Ver detalhes completos"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Stack>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Paginação */}
          {total > itemsPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={Math.ceil(total / itemsPerPage)}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {/* Dialog de Detalhes */}
      <Dialog
        open={detailDialog}
        onClose={() => setDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Gavel sx={{ mr: 1 }} />
            <Typography variant="h6">Detalhes da Jurisprudência</Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedItem ? (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {selectedItem.title}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Tribunal
                  </Typography>
                  <Typography variant="body2">{selectedItem.court_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Órgão Julgador
                  </Typography>
                  <Typography variant="body2">{selectedItem.judging_body || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Data do Julgamento
                  </Typography>
                  <Typography variant="body2">{formatDate(selectedItem.judgment_date)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Número CNJ
                  </Typography>
                  <Typography variant="body2">{selectedItem.cnj_number || '-'}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Ementa
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedItem.ementa}
                </Typography>
              </Paper>

              {selectedItem.binding_thesis && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Tese Vinculante
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'info.50' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedItem.binding_thesis}
                    </Typography>
                  </Paper>
                </>
              )}

              {selectedItem.summary && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Resumo
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedItem.summary}
                    </Typography>
                  </Paper>
                </>
              )}

              {selectedItem.keywords && selectedItem.keywords.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Palavras-chave
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {selectedItem.keywords.map((keyword, idx) => (
                      <Chip key={idx} label={keyword} size="small" />
                    ))}
                  </Stack>
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Feedback para pesquisa de jurisprudência */}
      {results.length > 0 && (
        <FeedbackWidget
          feature="jurisprudence_search"
          title="Avalie os resultados da pesquisa"
          position="bottom-left"
          compact
        />
      )}
    </Container>
  );
};

export default JurisprudenceSearchPage;

